import { useState, useCallback } from 'react'

// Available dithering algorithms
export type DitherAlgorithm =
  | 'floydSteinberg' // Classic Floyd-Steinberg error diffusion
  | 'ordered'        // Bayer matrix ordered dithering (very fast)
  | 'atkinson'       // Atkinson dithering (good for e-ink, preserves detail)
  | 'stucki'         // Stucki dithering (higher quality error diffusion)

export const DITHER_ALGORITHMS: { value: DitherAlgorithm; label: string; description: string }[] = [
  { value: 'floydSteinberg', label: 'Floyd-Steinberg', description: 'Classic error diffusion, smooth gradients' },
  { value: 'atkinson', label: 'Atkinson', description: 'Preserves detail, good for e-ink' },
  { value: 'ordered', label: 'Ordered (Bayer)', description: 'Fast, patterned dithering' },
  { value: 'stucki', label: 'Stucki', description: 'High quality, wider diffusion' },
]

export interface ImageTransformSettings {
  ditherAlgorithm: DitherAlgorithm
  rotation: 0 | 90 | 180 | 270
  flipHorizontal: boolean
  flipVertical: boolean
  scale: number  // 1.0 = fit to display, >1 = zoom in
  panX: number   // Pan offset in pixels (at scale 1.0)
  panY: number
  crop: {
    enabled: boolean
    x: number
    y: number
    width: number
    height: number
  }
}

export interface EditorImage {
  id: string
  file?: File
  blob?: Blob
  sourceUrl: string
  filename: string
  date: string
  previewUrl: string
  settings: ImageTransformSettings
  processedPreview?: string  // Preview after transforms
  processedData?: Uint8Array // Final BMP data
}

const DEFAULT_SETTINGS: ImageTransformSettings = {
  ditherAlgorithm: 'floydSteinberg',
  rotation: 0,
  flipHorizontal: false,
  flipVertical: false,
  scale: 1.0,
  panX: 0,
  panY: 0,
  crop: {
    enabled: false,
    x: 0,
    y: 0,
    width: 400,
    height: 600,
  },
}

export function useImageEditor() {
  const [images, setImages] = useState<EditorImage[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isProcessing, setIsProcessing] = useState(false)

  const addImages = useCallback((newImages: Omit<EditorImage, 'settings'>[]) => {
    setImages(prev => [
      ...prev,
      ...newImages.map(img => ({
        ...img,
        settings: { ...DEFAULT_SETTINGS },
      })),
    ])
    // Select first image if none selected
    if (images.length === 0 && newImages.length > 0) {
      setSelectedIndex(0)
    }
  }, [images.length])

  const removeImage = useCallback((id: string) => {
    setImages(prev => {
      const newImages = prev.filter(img => img.id !== id)
      // Adjust selected index if needed
      if (selectedIndex >= newImages.length && newImages.length > 0) {
        setSelectedIndex(newImages.length - 1)
      }
      return newImages
    })
  }, [selectedIndex])

  const updateSettings = useCallback((id: string, updates: Partial<ImageTransformSettings>) => {
    setImages(prev => prev.map(img =>
      img.id === id
        ? { ...img, settings: { ...img.settings, ...updates } }
        : img
    ))
  }, [])

  const setDitherAlgorithm = useCallback((id: string, algorithm: DitherAlgorithm) => {
    updateSettings(id, { ditherAlgorithm: algorithm })
  }, [updateSettings])

  const setScale = useCallback((id: string, scale: number) => {
    updateSettings(id, { scale: Math.max(1, Math.min(4, scale)) })
  }, [updateSettings])

  const setPan = useCallback((id: string, panX: number, panY: number) => {
    updateSettings(id, { panX, panY })
  }, [updateSettings])

  const setRotation = useCallback((id: string, rotation: 0 | 90 | 180 | 270) => {
    updateSettings(id, { rotation })
  }, [updateSettings])

  const rotateClockwise = useCallback((id: string) => {
    setImages(prev => prev.map(img => {
      if (img.id !== id) return img
      const rotations: (0 | 90 | 180 | 270)[] = [0, 90, 180, 270]
      const currentIdx = rotations.indexOf(img.settings.rotation)
      const newRotation = rotations[(currentIdx + 1) % 4]
      return { ...img, settings: { ...img.settings, rotation: newRotation } }
    }))
  }, [])

  const rotateCounterClockwise = useCallback((id: string) => {
    setImages(prev => prev.map(img => {
      if (img.id !== id) return img
      const rotations: (0 | 90 | 180 | 270)[] = [0, 90, 180, 270]
      const currentIdx = rotations.indexOf(img.settings.rotation)
      const newRotation = rotations[(currentIdx + 3) % 4]
      return { ...img, settings: { ...img.settings, rotation: newRotation } }
    }))
  }, [])

  const toggleFlipHorizontal = useCallback((id: string) => {
    setImages(prev => prev.map(img =>
      img.id === id
        ? { ...img, settings: { ...img.settings, flipHorizontal: !img.settings.flipHorizontal } }
        : img
    ))
  }, [])

  const toggleFlipVertical = useCallback((id: string) => {
    setImages(prev => prev.map(img =>
      img.id === id
        ? { ...img, settings: { ...img.settings, flipVertical: !img.settings.flipVertical } }
        : img
    ))
  }, [])

  const setCrop = useCallback((id: string, crop: ImageTransformSettings['crop']) => {
    updateSettings(id, { crop })
  }, [updateSettings])

  const setDate = useCallback((id: string, newDate: Date) => {
    setImages(prev => prev.map(img => {
      if (img.id !== id) return img

      const month = String(newDate.getMonth() + 1).padStart(2, '0')
      const day = String(newDate.getDate()).padStart(2, '0')
      const year = newDate.getFullYear()
      const date = `${month}-${day}`

      // Rebuild filename: extract the suffix after MM-DD_YYYY
      const oldFilename = img.filename
      // Match pattern: MM-DD_YYYY_suffix.bmp or MM-DD_YYYY.bmp
      const match = oldFilename.match(/^\d{2}-\d{2}_\d{4}(.*)$/)
      const suffix = match ? match[1] : '.bmp'
      const filename = `${month}-${day}_${year}${suffix}`

      return { ...img, date, filename }
    }))
  }, [])

  const resetSettings = useCallback((id: string) => {
    setImages(prev => prev.map(img =>
      img.id === id
        ? { ...img, settings: { ...DEFAULT_SETTINGS } }
        : img
    ))
  }, [])

  const setProcessedData = useCallback((id: string, processedData: Uint8Array, processedPreview?: string) => {
    setImages(prev => prev.map(img =>
      img.id === id
        ? { ...img, processedData, processedPreview }
        : img
    ))
  }, [])

  const clearAll = useCallback(() => {
    setImages([])
    setSelectedIndex(0)
  }, [])

  const selectedImage = images[selectedIndex] || null

  return {
    images,
    selectedIndex,
    selectedImage,
    isProcessing,
    setSelectedIndex,
    setIsProcessing,
    addImages,
    removeImage,
    updateSettings,
    setDitherAlgorithm,
    setScale,
    setPan,
    setRotation,
    rotateClockwise,
    rotateCounterClockwise,
    toggleFlipHorizontal,
    toggleFlipVertical,
    setCrop,
    setDate,
    resetSettings,
    setProcessedData,
    clearAll,
  }
}
